import dayjs from 'dayjs'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'

const toArray = (v) => Array.isArray(v) ? v : (v?.data?.results ?? v?.data ?? v?.results ?? [])

/**
 * Shared stock-availability logic used by the allocation confirmation flows.
 * Mirrors the logic in WarehouseAvailabilityPivotGrid (include_scheduled=true).
 */
export function useAllocationAvailability() {
  const planningStore = usePlanningStore()

  /**
   * Projected availability at the project start date.
   * Returns a map: { "warehouseId_serviceId": availableQuantity }.
   */
  const loadAvailabilityAtDay = async(serviceIds, day) => {
    if (!day || !serviceIds.length) return {}
    const dateFrom = dayjs(day).startOf('day').toISOString()
    const dateTo = dayjs(day).startOf('day').add(1, 'day').toISOString()
    const res = await planningApi.getStockAvailabilityAsync({
      service_ids: serviceIds.join(','),
      date_from: dateFrom,
      date_to: dateTo,
      include_scheduled: true,
    })
    const data = res?.data ?? res ?? {}
    const map = {}
    for (const [key, intervals] of Object.entries(data)) {
      map[key] = intervals?.length ? Number(intervals[0].quantity) : 0
    }
    return map
  }

  const loadAvailabilityAtProjectStart = (serviceIds, projectStart) => loadAvailabilityAtDay(serviceIds, projectStart)

  /**
   * Build warehouse allocations from availability data.
   * Derives available warehouses from the availability response (not from current stock).
   */
  const buildAllocationsFromAvailability = (availability, serviceId, warehouseNamesById = {}) => {
    const allocations = []
    for (const [key, qty] of Object.entries(availability)) {
      const [whIdStr, svcIdStr] = key.split('_')
      if (parseInt(svcIdStr) !== serviceId) continue
      const warehouseId = parseInt(whIdStr)
      if (qty <= 0) continue
      allocations.push({
        warehouseId,
        warehouseName: warehouseNamesById[warehouseId] || `Location #${warehouseId}`,
        available: qty,
        quantity: 0,
      })
    }
    return allocations
  }

  const fetchWarehouseNamesById = async() => {
    const warehousesRes = await planningStore.fetchWarehousesAsync({ all: true })
    const warehouses = toArray(warehousesRes).filter(w => !w.archived)
    const map = {}
    for (const w of warehouses) {
      map[w.id] = w.name
    }
    return map
  }

  return { loadAvailabilityAtDay, loadAvailabilityAtProjectStart, buildAllocationsFromAvailability, fetchWarehouseNamesById }
}
